import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import dashboard from "../../assets/dashboard-1.jpg";

import { Doughnut } from 'react-chartjs-2';
import Chart from 'chart.js/auto';
import { ArcElement, Tooltip, Legend } from "chart.js";
import { Calendar, momentLocalizer } from "react-big-calendar";
import moment from "moment";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "moment/locale/pt-br"; // Set locale for Portuguese
import API from "../../utils/API";

Chart.register(ArcElement, Tooltip, Legend);

function DoughnutChart(value, remaining, plugin, color) {
  return (
    <Doughnut
      data={{
        labels: ["Completed", "Remaining"],
        datasets: [
          {
            data: [value, remaining],
            backgroundColor: [color, "#E0E0E0"],
            borderWidth: 0,
          },
        ],
      }}
      options={{
        plugins: {
          legend: { display: false },
        },
        cutout: "70%",
      }}
      plugins={[plugin]}
    />
  );
}

const centerTextPlugin = {
  id: 'centerText',
  beforeDraw: function (chart) {
    let width = chart.width, height = chart.height;
    let ctx = chart.ctx;
    ctx.restore();
    let fontSize = (height / 10).toFixed(2);
    ctx.font = fontSize + "px Arial";
    ctx.textBaseline = "middle";

    let text = `${chart.data.datasets[0].data[0]}%`,
      textX = Math.round((width - ctx.measureText(text).width) / 2),
      textY = height / 2;

    ctx.fillText(text, textX, textY);
    ctx.save();
  }
};

moment.locale("pt-br"); // Use Portuguese locale
const localizer = momentLocalizer(moment);

const handleEventClick = (event) => {
  window.location.href = `/order?id=${event.id}`;
};

function Home() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [userRole, setUserRole] = useState("");

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("yc_usr_dt_ls"))
    if (user && ["MECANICO", "COMPRADOR"].includes(user.role)) {
      window.location.href = '/services';
    }
    if (user) setUserRole(user.role || "");
  },[])

  // Format currency (Brazilian Real)
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  useEffect(() => {
    setLoading(true);
    
    if(!localStorage.getItem('yc_usr_dt_ls')) {
      window.location.href = '/login';
    }

    // Fetch services for calendar
    API.getServices(API.cancelToken()).then((res) => {
      const newEvents = res.data.map((item) => ({
        id: item.service_id,
        title: item.problem_description,
        start: new Date(item.expected_delivery),
        end: new Date(item.expected_delivery),
        description: item.problem_description,
      }));
      setEvents(newEvents);
      setLoading(false);
    }).catch(err => {
      console.error("Error fetching services:", err);
      setLoading(false);
    });

    // In a real implementation, you would fetch stats from your API
    // API.getStatistics(API.cancelToken()).then((res) => {
    //   setStats(res.data);
    // }).catch(err => {
    //   console.error("Error fetching statistics:", err);
    // });

    API.getStatistics(API.cancelToken(), "month").then((res) => {
      setStats(res.data);
    })

  }, []);

  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="container mx-auto px-4 py-6">
        {/* Welcome Banner */}
        <div className="bg-white rounded-lg shadow-md mb-6 overflow-hidden">
          <div className="flex flex-col md:flex-row">
            <div className="w-full md:w-1/3">
              <img
                src={dashboard}
                alt="Dashboard Banner"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="w-full md:w-2/3 p-6 flex flex-col justify-center">
              <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-2">
                Bem-vindo ao Sistema de Gerenciamento You Connected
              </h1>
              <p className="text-gray-600 mb-4">
                Gerencie seus serviços, acompanhe seu calendário e visualize estatísticas em um só lugar.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link 
                  to="/create/service" 
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md transition-all duration-200"
                >
                  Criar Serviço
                </Link>
                <Link 
                  to="/schedule" 
                  className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-2 rounded-md transition-all duration-200"
                >
                  Acessar agenda
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Statistics Cards */}
        {stats ? 
          <div className="mb-6">
            <h2 className="text-xl font-semibold mb-4 text-gray-800 px-1">Estatísticas Gerais</h2>
            {/* Row 1: totals */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <StatsCard
                title="Total de Serviços"
                value={stats.totalServices}
                icon="📋"
                color="bg-blue-100"
                textColor="text-blue-800"
              />
              <StatsCard
                title="Veículos"
                value={stats.totalVehicles}
                icon="🚗"
                color="bg-purple-100"
                textColor="text-purple-800"
              />
            </div>

            {/* Row 2: service stats (2 cards for RECEPCAO, 3 otherwise) */}
            <div className={`grid grid-cols-1 sm:grid-cols-2 ${userRole === "RECEPCAO" ? "" : "lg:grid-cols-3"} gap-4`}>
              <StatsCard
                title="Em Progresso"
                value={stats.servicesInProgress}
                icon="⚙️"
                color="bg-yellow-100"
                textColor="text-yellow-800"
              />
              <StatsCard
                title="Concluídos"
                value={stats.servicesCompleted}
                icon="✅"
                color="bg-green-100"
                textColor="text-green-800"
              />
              {userRole !== "RECEPCAO" && (
                <StatsCard
                  title="Ticket Médio"
                  value={formatCurrency(stats.averageServicePrice)}
                  icon="💰"
                  color="bg-indigo-100"
                  textColor="text-indigo-800"
                />
              )}
            </div>
          </div>
          :
          <div className="mb-6 bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500 mr-3"></div>
              <h2 className="text-xl font-medium text-gray-700">Carregando estatísticas do mês...</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-7 gap-4 mt-6">
              {[...Array(7)].map((_, index) => (
                <div key={index} className="bg-gray-100 rounded-lg shadow-sm p-4 animate-pulse">
                  <div className="flex items-center">
                    <div className="rounded-full bg-gray-200 h-10 w-10 mr-4"></div>
                    <div className="flex-1">
                      <div className="h-3 bg-gray-200 rounded w-3/4 mb-2"></div>
                      <div className="h-5 bg-gray-200 rounded w-1/2"></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        }

        <div className="mb-6">
          {/* Calendar Card */}
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="bg-gray-50 px-4 py-3 border-b flex justify-between items-center">
              <h3 className="text-lg font-semibold text-gray-800">Agenda da Semana</h3>
              <Link to="/schedule" className="text-blue-600 hover:text-blue-800 transition-colors">
                Ver agenda completa
              </Link>
            </div>
            <div className="p-4">
              {loading ? (
                <div className="flex justify-center items-center h-64">
                  <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-500"></div>
                </div>
              ) : (
                <Calendar
                  localizer={localizer}
                  events={events}
                  startAccessor="start"
                  endAccessor="end"
                  defaultView="week"
                  onSelectEvent={handleEventClick}
                  style={{ height: 400 }}
                  messages={{
                    next: "Próximo",
                    previous: "Anterior",
                    today: "Hoje",
                    month: "Mês",
                    week: "Semana",
                    day: "Dia",
                    agenda: "Agenda",
                    noEventsInRange: "Nenhum evento para este período",
                  }}
                />
              )}
            </div>
          </div>
        </div>

        {/* Performance Metrics 
        <div className="bg-white rounded-lg shadow-md overflow-hidden mb-6">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold text-gray-800">Métricas de Desempenho</h2>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">

              <div className="flex flex-col items-center">
                <div className="w-40 h-40">
                  {DoughnutChart(15, 85, centerTextPlugin, 'rgba(239, 68, 68, 1)')}
                </div>
                <p className="text-center mt-4 font-medium text-gray-700">Ordens Canceladas</p>
              </div>

        
              <div className="flex flex-col items-center">
                <div className="w-40 h-40">
                  {DoughnutChart(70, 30, centerTextPlugin, 'rgba(59, 130, 246, 1)')}
                </div>
                <p className="text-center mt-4 font-medium text-gray-700">Tempo Médio de Conclusão (dias)</p>
              </div>

  
              <div className="flex flex-col items-center">
                <div className="w-40 h-40">
                  {DoughnutChart(25, 75, centerTextPlugin, 'rgba(16, 185, 129, 1)')}
                </div>
                <p className="text-center mt-4 font-medium text-gray-700">Ordens Atrasadas</p>
              </div>
            </div>
          </div>
        </div>
        */}
      </div>
    </div>
  );
}

// Component for statistics cards
const StatsCard = ({ title, value, icon, color, textColor }) => (
  <div className={`p-4 rounded-lg shadow-sm overflow-hidden ${color}`}>
    <div className="flex items-center gap-3 min-w-0">
      <div className={`text-2xl xl:text-3xl shrink-0 ${textColor}`}>{icon}</div>
      <div className="min-w-0 flex-1">
        <p className="text-xs xl:text-sm text-gray-600 truncate" title={title}>{title}</p>
        <p
          className={`text-lg xl:text-xl font-bold leading-tight break-words ${textColor}`}
          title={typeof value === "string" ? value : undefined}
        >
          {value}
        </p>
      </div>
    </div>
  </div>
);

export default Home;